'use client'

import { Button, html } from '@ensemble.music/ui'
import { X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef } from 'react'
import { navSections } from './content'

/**
 * The full-width mobile sheet behind the menu button. It covers the page below the bar, so there
 * is no backdrop to dim and nothing behind it to click by accident. Escape closes it, Tab stays
 * inside it, the body does not scroll while it is open and focus goes back to the trigger.
 */
export function MobileMenu({
  open,
  onClose,
  onStart,
}: {
  open: boolean
  onClose: () => void
  onStart: () => void
}) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const trigger = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusable = () => [
      ...(panel.current?.querySelectorAll<HTMLElement>('a[href], button') ?? []),
    ]
    focusable()[0]?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusable()
      const first = items[0]
      const last = items.at(-1)
      if (!first || !last) return
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      trigger?.focus()
    }
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="landing-sheet"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <html.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label="Hauptnavigation"
            className="flex flex-col gap-2 w-full h-full bg-background border-t border-border-color p-4"
          >
            <html.div className="flex flex-row justify-end">
              <Button
                type="button"
                tone="ghost"
                aria-label="Menü schließen"
                onClick={onClose}
              >
                <X
                  size={20}
                  aria-hidden="true"
                />
              </Button>
            </html.div>

            <html.nav
              aria-label="Hauptnavigation"
              className="flex flex-col"
            >
              {navSections.map((section) => (
                <html.a
                  key={section.id}
                  href={`#${section.id}`}
                  onClick={onClose}
                  className="flex flex-row items-center min-h-[52px] px-2 rounded-xl text-lg font-semibold text-color no-underline hover:bg-primary-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring"
                >
                  {section.label}
                </html.a>
              ))}
            </html.nav>

            <Button
              type="button"
              size="lg"
              onClick={() => {
                onClose()
                onStart()
              }}
            >
              Anfrage starten
            </Button>
          </html.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
